import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Icon,
  PrimaryButton,
} from "@fluentui/react";
import React, { useContext, useState } from "react";
import { components } from "react-select";
import styled, { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { Select } from "../select/Select";
import { ColumnOptionsContext } from "./ColumnOptions";

export const ViewSelect = ({ className }: { className?: string }) => {
  const theme = useTheme() as IStyledTheme;
  const {
    views,
    selectedView,
    isViewLoading,
    onChangeView,
    strings,
    hideViewOptionsFeatures,
    usePlaceholder,
    hasUnsavedChanges,
    t,
    id,
  } = useContext(ColumnOptionsContext);

  const [confirm, setConfirm] = useState<{
    open: boolean;
    pendingId: number | null;
  }>({ open: false, pendingId: null });

  // When switching/clearing the view with unsaved edits, ask before discarding.
  const requestChangeView = (newId: number | null) => {
    if (hasUnsavedChanges) {
      setConfirm({ open: true, pendingId: newId });
    } else {
      onChangeView(newId);
    }
  };

  const closeConfirm = () => setConfirm({ open: false, pendingId: null });

  const options = views.map((s) => {
    return {
      label: s.userAppSettingName,
      value: s.userAppSettingId,
      isDefault: s.isDefault,
    };
  });

  let selectedOption = null;
  if (selectedView !== null && selectedView !== undefined) {
    selectedOption = {
      value: selectedView.userAppSettingId,
      label: selectedView.userAppSettingName,
      isDefault: selectedView.isDefault,
    };
  }
  //
  else if (!usePlaceholder) {
    selectedOption = {
      value: "default",
      label: "Default",
    };
  }

  const isLoading = isViewLoading;
  const isDisabled = isLoading;

  const isDark = theme?.isDark || false;

  return (
    <ViewSelectContainer className={className}>
      {!hideViewOptionsFeatures && (
        <>
          <Select
            value={selectedOption}
            onChange={(o: any) => {
              requestChangeView(o ? o.value : null);
            }}
            options={options}
            noOptionsMessage={() => strings.viewSelectNoViewsLabel}
            placeholder={strings.viewSelectPlaceholder}
            isSearchable={options.length > 8}
            isClearable={!!selectedView}
            loadingMessage={() => strings.viewSelectLoadingLabel}
            isDisabled={isDisabled}
            isLoading={isLoading}
            styles={{
              menu: (provided) => ({
                ...provided,
                backgroundColor: isDark ? "#292827" : "white",
                zIndex: 9999,
              }),
              menuList: (provided) => ({
                ...provided,
                backgroundColor: isDark ? "#292827" : "white",
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected
                  ? isDark
                    ? "#3b3a39"
                    : "rgb(237, 235, 233)"
                  : state.isFocused
                  ? isDark
                    ? "#201f1e"
                    : "rgb(243, 242, 241)"
                  : isDark
                  ? "#292827"
                  : "white",
                "&:hover": {
                  backgroundColor: state.isSelected
                    ? isDark
                      ? "#3b3a39"
                      : "rgb(237, 235, 233)"
                    : isDark
                    ? "#201f1e"
                    : "rgb(243, 242, 241)",
                },
              }),
            }}
            components={{
              Option: CustomOption,
              SingleValue: CustomSingleValue,
              DropdownIndicator: (props: any) => (
                <components.DropdownIndicator {...props}>
                  <span style={{ fontSize: "12px" }}>▼</span>
                </components.DropdownIndicator>
              ),
            }}
            id={id ? `${id}-view-select` : undefined}
            className="view-select"
          />
        </>
      )}
      <Dialog
        hidden={!confirm.open}
        onDismiss={closeConfirm}
        dialogContentProps={{
          type: DialogType.normal,
          title: t("greco.unsavedChanges", "Unsaved changes"),
          subText: t(
            "greco.discardChangesConfirm",
            "You have unsaved changes to the current view. Discard them and switch?"
          ),
        }}
        modalProps={{ isBlocking: true }}
      >
        <DialogFooter>
          <DefaultButton
            onClick={closeConfirm}
            text={t("greco.cancel", "Cancel")}
          />
          <PrimaryButton
            onClick={() => {
              const pendingId = confirm.pendingId;
              closeConfirm();
              onChangeView(pendingId);
            }}
            text={t("greco.discard", "Discard")}
          />
        </DialogFooter>
      </Dialog>
    </ViewSelectContainer>
  );
};

const ViewSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StarIcon = () => <StyledStarIcon iconName="FavoriteStarFill" />;

const CustomOption = (props: any) => {
  return (
    <components.Option {...props}>
      <OptionContent>
        {props.data.isDefault && <StarIcon />}
        {props.children}
      </OptionContent>
    </components.Option>
  );
};

const CustomSingleValue = (props: any) => {
  return (
    <components.SingleValue {...props}>
      <SingleValueContent>
        {props.data.isDefault && <StarIcon />}
        {props.children}
      </SingleValueContent>
    </components.SingleValue>
  );
};

const OptionContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SingleValueContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StyledStarIcon = styled(Icon)`
  color: ${(props) => props.theme.palette.themePrimary};
  font-size: 16px;
`;
